import { useEffect, useRef, useState, useCallback } from 'react';
import { X, Plus, Minus, RotateCcw } from 'lucide-react';

interface ImageZoomModalProps {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
  dir?: 'ltr' | 'rtl';
  hint?: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;

export function ImageZoomModal({ src, alt, isOpen, onClose, dir = 'ltr', hint }: ImageZoomModalProps) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ active: boolean; startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const pinchRef = useRef<{ active: boolean; startDist: number; initialScale: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const interactionReadyRef = useRef(false);

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      reset();
      interactionReadyRef.current = false;
      const timer = window.setTimeout(() => {
        interactionReadyRef.current = true;
      }, 350);
      return () => {
        window.clearTimeout(timer);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, reset]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setScale((s) => Math.min(s * 1.2, MAX_SCALE));
      if (e.key === '-') setScale((s) => Math.max(s / 1.2, MIN_SCALE));
      if (e.key === '0') reset();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, reset]);

  if (!isOpen) return null;

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
    const nextScale = Math.min(Math.max(scale * Math.exp(-dy * 0.0015), MIN_SCALE), MAX_SCALE);
    const k = nextScale / scale;
    setPos((prev) => ({
      x: prev.x + (px - rect.width / 2) * (1 - k),
      y: prev.y + (py - rect.height / 2) * (1 - k),
    }));
    setScale(nextScale);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      initialX: pos.x,
      initialY: pos.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current?.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({
      x: dragRef.current.initialX + dx,
      y: dragRef.current.initialY + dy,
    });
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const getTouchDistance = (t1: { clientX: number; clientY: number }, t2: { clientX: number; clientY: number }) => {
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!interactionReadyRef.current) return;
    if (e.touches.length === 1) {
      const t = e.touches[0];
      dragRef.current = {
        active: true,
        startX: t.clientX,
        startY: t.clientY,
        initialX: pos.x,
        initialY: pos.y,
      };
    } else if (e.touches.length === 2) {
      pinchRef.current = {
        active: true,
        startDist: getTouchDistance(e.touches[0], e.touches[1]),
        initialScale: scale,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!interactionReadyRef.current) return;
    if (e.touches.length === 1 && dragRef.current?.active) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.startX;
      const dy = t.clientY - dragRef.current.startY;
      setPos({
        x: dragRef.current.initialX + dx,
        y: dragRef.current.initialY + dy,
      });
    } else if (e.touches.length === 2 && pinchRef.current?.active) {
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      const nextScale = Math.min(Math.max((pinchRef.current.initialScale * dist) / pinchRef.current.startDist, MIN_SCALE), MAX_SCALE);
      setScale(nextScale);
    }
  };

  const handleTouchEnd = () => {
    dragRef.current = null;
    pinchRef.current = null;
  };

  const zoomIn = () => setScale((s) => Math.min(s * 1.3, MAX_SCALE));
  const zoomOut = () => setScale((s) => Math.max(s / 1.3, MIN_SCALE));

  return (
    <div
      dir={dir}
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Zoom out"
            type="button"
          >
            <Minus className="w-5 h-5" />
          </button>
          <button
            onClick={zoomIn}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Zoom in"
            type="button"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            onClick={reset}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Reset zoom"
            type="button"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label="Close image zoom"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Viewport */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-hidden relative flex items-center justify-center cursor-grab active:cursor-grabbing touch-none p-4 sm:p-6"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex max-w-full max-h-full items-center justify-center pointer-events-none"
          style={{ transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`, transformOrigin: 'center' }}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            onLoad={reset}
            className="block w-auto h-auto max-w-full max-h-[calc(100dvh-7rem)] object-contain select-none"
          />
        </div>
        {hint && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white/90 text-sm pointer-events-none">
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}
